import type { Theme } from '@mui/material';
import {
  Box,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ResponsiveBar } from '@nivo/bar';
import type { FC } from 'react';
import { useMemo } from 'react';

const days = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const monthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const nivoTheme = (theme: Theme): Record<string, unknown> => ({
  axis: {
    domain: {
      line: {
        stroke: '#777777',
        strokeWidth: 1,
      },
    },
    ticks: {
      line: {
        stroke: '#777777',
        strokeWidth: 1,
      },
    },
  },
  fontSize: 11,
  grid: {
    line: {
      stroke: '#333333',
      strokeWidth: 1,
    },
  },
  // 'background': '#ffffff',
  textColor: theme.palette.text.primary,
  tooltip: {
    container: {
      background: theme.palette.background.paper,
      fontSize: 12,
    },
  },
});

export interface PatrolsSummaryData {
  byDayOfWeek: {
    _id: {
      dayOfWeek: number;
      status: string;
    };
    count: number;
  }[];
  byMonth: {
    _id: {
      year: number;
      month: number;
      status: string;
    };
    count: number;
  }[];
  byStatus: {
    _id: {
      status: string;
    };
    count: number;
  }[];
}

const indexOf = (value: { _id: { month: number; year: number } }): number =>
  value._id.year * 12 + value._id.month - 1;
const toTickName = (index: number): string => {
  const month = index % 12;
  const year = Math.floor(index / 12);

  return `${monthNames[month]} '${year % 100}`;
};

export const PatrolsSummaryView: FC<{
  data: PatrolsSummaryData;
  statuses: string[];
}> = ({ data, statuses }) => {
  const theme = useTheme();
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const monthData = useMemo(() => {
    if (!data.byMonth.length) return [];

    const sortedMonths = data.byMonth.sort((a, b) => {
      if (a._id.year === b._id.year) return a._id.month - b._id.month;
      return a._id.year - b._id.year;
    });
    const min = indexOf(sortedMonths[0]);
    const max = indexOf(sortedMonths[sortedMonths.length - 1]);
    return sortedMonths.reduce(
      (acc, row) => {
        const index = indexOf(row) - min;
        acc[index] = {
          ...acc[index],
          [row._id.status]: row.count,
        };
        return acc;
      },
      Array.from(new Array(max - min + 1)).map((_, i) => ({
        name: toTickName(i + min),
      })),
    );
  }, [data, smallScreen]);

  const dayOfWeekData = useMemo(
    () =>
      data.byDayOfWeek
        .sort((a, b) => a._id.dayOfWeek - b._id.dayOfWeek)
        .reduce(
          (acc, row) => {
            const index = row._id.dayOfWeek - 1;
            acc[index] = {
              ...acc[index],
              [row._id.status]: row.count,
            };
            return acc;
          },
          Array.from(new Array(7)).map((_, i) => ({
            name: smallScreen ? days[i].slice(0, 2) : days[i],
          })),
        ),
    [data, smallScreen],
  );

  if (!data.byDayOfWeek.length || !data.byMonth.length) {
    return (
      <CardContent>
        <Typography sx={{ color: 'GrayText', fontStyle: 'italic' }}>
          No patrols in last 5 years
        </Typography>
      </CardContent>
    );
  }

  return (
    <Box>
      <Typography sx={{ mt: 3, textAlign: 'center' }}>
        Patrols in last 5 years
      </Typography>
      {!smallScreen && (
        <Box sx={{ height: 200 }}>
          <ResponsiveBar
            axisBottom={{
              legendOffset: 32,
              legendPosition: 'middle',
              tickPadding: 5,
              tickRotation: -90,
              tickSize: 5,
            }}
            axisLeft={{
              legend: 'Patrols',
              legendOffset: -40,
              legendPosition: 'middle',
              tickPadding: 5,
              tickRotation: 0,
              tickSize: 5,
            }}
            borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
            colors={{ scheme: 'set1' }}
            data={monthData}
            indexBy="name"
            indexScale={{ round: true, type: 'band' }}
            keys={statuses}
            labelSkipHeight={32}
            labelSkipWidth={32}
            legends={[
              {
                anchor: 'bottom-right',
                dataFrom: 'keys',
                direction: 'column',
                effects: [
                  {
                    on: 'hover',
                    style: {
                      itemOpacity: 1,
                    },
                  },
                ],
                itemDirection: 'left-to-right',
                itemHeight: 20,
                itemOpacity: 0.85,
                itemsSpacing: 2,
                itemWidth: 100,
                justify: false,
                symbolSize: 20,
                translateX: 120,
                translateY: 0,
              },
            ]}
            margin={{ bottom: 54, left: 60, right: 130, top: 24 }}
            padding={0.3}
            theme={nivoTheme(theme)}
            valueFormat=""
            valueScale={{ type: 'linear' }}
          />
        </Box>
      )}
      <Box sx={{ height: 200 }}>
        <ResponsiveBar
          axisLeft={{
            legend: 'Patrols',
            legendOffset: -40,
            legendPosition: 'middle',
            tickPadding: 5,
            tickRotation: 0,
            tickSize: 5,
          }}
          borderColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
          colors={{ scheme: 'set1' }}
          data={dayOfWeekData}
          indexBy="name"
          indexScale={{ round: true, type: 'band' }}
          keys={statuses}
          legends={[
            {
              anchor: 'bottom-right',
              dataFrom: 'keys',
              direction: 'column',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemOpacity: 1,
                  },
                },
              ],
              itemDirection: 'left-to-right',
              itemHeight: 20,
              itemOpacity: 0.85,
              itemsSpacing: 2,
              itemWidth: 100,
              justify: false,
              symbolSize: 20,
              translateX: 120,
              translateY: 0,
            },
          ]}
          margin={{ bottom: 44, left: 60, right: 130, top: 24 }}
          padding={0.3}
          theme={nivoTheme(theme)}
          valueFormat=""
          valueScale={{ type: 'linear' }}
        />
      </Box>
    </Box>
  );
};
